import { NewIdeaDialog } from "@/components/ideas/new-idea-dialog";
import { AppLink } from "@/components/ui/app-link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { LocalRelative } from "@/components/ui/local-date";
import { api } from "@/lib/api";
import { workItemHref } from "@/lib/project-href";
import { Link, createFileRoute } from "@tanstack/react-router";
import { z } from "zod";

/**
 * `/ideas` — lightweight pre-issue list. In-place "New Idea"
 * modal opens via `<NewIdeaDialog>` (port of the Next version
 * with the MarkdownEditor / Combobox replaced by plain textarea
 * / select until those primitives port too).
 */

const STATUS_FILTERS = ["open", "converted", "rejected", "all"] as const;
type StatusFilter = (typeof STATUS_FILTERS)[number];

const ideasSearchSchema = z.object({
  status: z.enum(STATUS_FILTERS).optional(),
});

type ListedIdea = {
  id: string;
  title: string;
  description: string | null;
  status: "open" | "converted" | "rejected";
  createdAt: string;
  author: { id: string; name: string | null };
  project: { id: string; name: string } | null;
  workItem: {
    id: string;
    gitlabIssueId: string;
    globalId: number;
    title: string;
    project: {
      id: string;
      slug: string;
      group: { path: string } | null;
    };
  } | null;
};

type IdeasResponse = {
  ideas: ListedIdea[];
  counts: { open: number; converted: number; rejected: number; all: number };
};

const statusVariant: Record<string, "secondary" | "success" | "outline"> = {
  open: "secondary",
  converted: "success",
  rejected: "outline",
};

export const Route = createFileRoute("/_app/ideas/")({
  validateSearch: ideasSearchSchema,
  loaderDeps: ({ search }) => ({ status: search.status }),
  loader: ({ deps }) => api.get<IdeasResponse>(`/api/ideas?status=${deps.status ?? "open"}`),
  component: IdeasPage,
});

function IdeasPage() {
  const { ideas, counts } = Route.useLoaderData();
  const search = Route.useSearch();
  const filter: StatusFilter = search.status ?? "open";

  const tabs: Array<{ key: StatusFilter; label: string; count: number }> = [
    { key: "open", label: "Open", count: counts.open },
    { key: "converted", label: "Converted", count: counts.converted },
    { key: "rejected", label: "Rejected", count: counts.rejected },
    { key: "all", label: "All", count: counts.all },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">Ideas</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Lightweight pre-issues. Promote good ones to a project work item.
          </p>
        </div>
        <NewIdeaDialog>
          <Button>New Idea</Button>
        </NewIdeaDialog>
      </div>

      <div className="flex gap-1 border-b">
        {tabs.map((t) => (
          <TabLink key={t.key} target={t.key} active={filter === t.key}>
            {t.label} <span className="text-xs text-muted-foreground">({t.count})</span>
          </TabLink>
        ))}
      </div>

      {ideas.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No ideas in this view.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {ideas.map((idea) => (
            <Card key={idea.id}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1 min-w-0 flex-1">
                    <AppLink
                      href={`/ideas/${idea.id}`}
                      className="font-heading text-base font-semibold text-primary hover:underline break-words"
                    >
                      {idea.title}
                    </AppLink>
                    {idea.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {idea.description}
                      </p>
                    )}
                    <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span>by {idea.author.name}</span>
                      <span>·</span>
                      <LocalRelative value={idea.createdAt} />
                      {idea.project && (
                        <>
                          <span>·</span>
                          <span>suggested for {idea.project.name}</span>
                        </>
                      )}
                      {idea.workItem && (
                        <>
                          <span>·</span>
                          <AppLink
                            href={workItemHref(idea.workItem.globalId)}
                            className="text-primary hover:underline"
                          >
                            #{idea.workItem.globalId} {idea.workItem.title}
                          </AppLink>
                        </>
                      )}
                    </div>
                  </div>
                  <Badge variant={statusVariant[idea.status] ?? "outline"}>{idea.status}</Badge>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function TabLink({
  target,
  active,
  children,
}: {
  target: StatusFilter;
  active: boolean;
  children: React.ReactNode;
}) {
  // Open tab is the default — drop `?status=` for that one so the URL
  // for "Open" stays the plain "/ideas".
  return (
    <Link
      to="/ideas"
      search={{ status: target === "open" ? undefined : target }}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
        active
          ? "border-primary text-primary"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </Link>
  );
}
